import { FileAddOutlined, FileTextOutlined } from "@ant-design/icons";
import * as Sentry from "@sentry/react";
import { Alert, Col, Divider, Drawer, Flex, Modal, Row, Tabs, message } from "antd";
import React from "react";
import { useDispatch } from "react-redux";
import { Outlet } from "react-router-dom";
import { useNetworkState } from "react-use";
import Swal from "sweetalert2";
import ActiveRecordingView from "../../components/ActiveRecordingView/ActiveRecordingView.component";
import ClientView from "../../components/ClientView/ClientView.component";
import CreateClient from "../../components/CreateClient/CreateClient.component";
import { AITemplateModal } from "../../components/CreateNoteModal/AITemplateModal/AITemplateModal.component";
import CreateNoteModalGCS from "../../components/CreateNoteModal/CreateNoteModalGCS.component";
import CustomiseNote from "../../components/Customise/customise-note.component";
import RecordCTA from "../../components/RecordCTA/RecordCTA.component";
import Share from "../../components/Share/Share.modal";
import { NoteType } from "../../domain/notes.domain";
import { isLastPlusOneNote } from "../../domain/user.domain";
import { useAppSelector } from "../../hooks/redux-hooks";
import { useIsMobileView } from "../../hooks/ui-hook";
import useIntercomWithData from "../../hooks/useIntercomWithData";
import { getAllDcmCodes } from "../../service/client.service";
import {
  getCustomizationList,
  handleCancelRecording,
  handleCaptureAudioSubmission,
  uploadAudioFromIndexedDBViaSignedURL,
} from "../../service/notes.service";
import { resumableUploadManager } from "../../service/upload.service";
import {
  setAddClientView,
  setAudioDevices,
  setCreateClientState,
  setCreateModalState,
  setCustomiseSectionState,
  setDemoState,
  setEditingState,
  setIsDemoPath,
  setNotesViewVisible,
  setOpenShare,
  setSelectedNoteForEditing,
  setShowRecordingView,
  setSubmissionProgressState,
  setUploadProgressState,
  setUploadType,
} from "../../slices/appStateSlice";
import { resetClientForm, setClientForm, setCustomizationList, setDsm } from "../../slices/clientSlice";
import {
  setIsPaused,
  setIsRecording,
  setMediaRecorder,
  setRecordingDetail,
  setRecordingDevice,
} from "../../slices/recordingsSlice";
import { setShowPaymentModal } from "../../slices/userSlice";
import type { AppDispatch } from "../../store";
import { EventType, trackEvent } from "../../utils/analytics";
import { assembleBlob, clearBuffers, getChunkStats, purgeExpired } from "../../utils/audioStore";
import {
  MAX_RECORDING_TIME_SECONDS,
  PURGE_EXPIRED_AUDIO_FILES_DAYS,
  RECORDER_TIMESLICE_MS_GCS,
} from "../../utils/constants";
import { eventEmitter } from "../../utils/eventEmitter";
import { fetchAudioInputDevices } from "../../utils/mediaRecorder.utils";
import { isTooShort, stopMediaRecorderAndStopTracks } from "../../utils/recording.utils";
import { releaseWakeLock, requestWakeLock } from "../../utils/wakeScreen";
import styles from "./home.module.scss";
import { checkMicrophoneMuteStatus } from "./utils";

const HomePage = () => {
  const componentName = "home";
  const dispatch = useDispatch<AppDispatch>();
  const isMobileView = useIsMobileView();

  const { isCustomiseSectionVisible, uploadType, showRecordingView, demoState } = useAppSelector(
    (state) => state.appState,
  );

  const networkState = useNetworkState();

  const { recordMimeType, mediaRecorder, isRecording, isPaused, recordingDetail, recordingDevice } = useAppSelector(
    (state) => state.recordings,
  );

  const { audioDevices } = useAppSelector((state) => state.appState);

  const { notesDetails } = useAppSelector((state) => state.notes);
  const userInfo = useAppSelector((state) => state.auth.userInfo);

  // Determine customization modal title based on note type
  const customizeModalTitle = React.useMemo(() => {
    if (notesDetails?.note_type === NoteType.SOAP || notesDetails?.note_type === NoteType.DAP) {
      return "Add instructions to fix this note";
    }
    return "Add One-Time Instructions to fix this note";
  }, [notesDetails?.note_type]);
  const userData = useAppSelector((state) => state.user.data);

  const { bootWithData } = useIntercomWithData();

  const [recordingTime, setRecordingTime] = React.useState<number>(0);
  const captureRecordingIdRef = React.useRef("");
  const recordingInterval = React.useRef<NodeJS.Timeout | null>(null);
  const [isMicrophoneMuted, setIsMicrophoneMuted] = React.useState(false);
  const createNoteModalRef = React.useRef<{
    cleanupAudioResources?: () => void;
    resumeRecording?: () => Promise<void>;
    clearFileUploads?: () => void;
  } | null>(null);

  const populateStore = () => {
    getAllDcmCodes()
      .then((response) => {
        const dsm = response.data;
        dispatch(setDsm(dsm));
      })
      .catch((error) => {
        console.error("Error fetching DCM codes:", error);
      });
    getCustomizationList()
      .then((response) => {
        dispatch(setCustomizationList(response.data));
      })
      .catch((error) => {
        console.error("Error fetching customization list:", error);
      });

    // to silently purge expired audio files
    purgeExpired(PURGE_EXPIRED_AUDIO_FILES_DAYS);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: populateStore updates every render
  React.useEffect(() => {
    dispatch(setIsDemoPath(false));
    populateStore();
    bootWithData();
  }, [dispatch]);

  const resetRecordingState = (withoutRefresh = false) => {
    if (mediaRecorder?.state !== "inactive") {
      stopMediaRecorderAndStopTracks(mediaRecorder);
    }

    // Cleanup audio resources in CreateNoteModal if available
    if (createNoteModalRef.current?.cleanupAudioResources) {
      createNoteModalRef.current.cleanupAudioResources();
    }

    // Clear file uploads (audio and PDFs) in CreateNoteModal if available
    if (createNoteModalRef.current?.clearFileUploads) {
      createNoteModalRef.current.clearFileUploads();
    }

    dispatch(setShowRecordingView(false));
    dispatch(setSubmissionProgressState(false));
    dispatch(setMediaRecorder(null));
    if (recordingInterval.current) {
      clearInterval(recordingInterval.current);
      recordingInterval.current = null;
    }
    setRecordingTime(0);
    dispatch(setUploadType(0));
    captureRecordingIdRef.current = "";
    dispatch(
      setRecordingDetail({
        title: "",
        note_type: undefined,
        category_type: 0,
        modality_type: "audio",
        gender_type: 0,
        language_type: 0,
        client_id: null,
        current_recording_id: null,
        fileList: [],
      }),
    );
    dispatch(resetClientForm());
    dispatch(setAddClientView(false));
    dispatch(setCreateModalState(false));
    dispatch(setEditingState(false));
    dispatch(setSelectedNoteForEditing(null));
    dispatch(setIsRecording(false));
    dispatch(setIsPaused(false));
    dispatch(setRecordingDevice(null));
    if (!withoutRefresh) {
      eventEmitter.emit("refreshNotes");
    }
    dispatch(setUploadProgressState(false));
  };

  const displayRecordingTimer = () => {
    // Start a timer to track recording time
    recordingInterval.current = setInterval(() => {
      if (recordingTime >= MAX_RECORDING_TIME_SECONDS) {
        return recordingTime;
      }
      setRecordingTime((time) => time + 1);
    }, 1000);
  };

  React.useEffect(() => {
    if (isRecording && recordingTime >= MAX_RECORDING_TIME_SECONDS) {
      handlePauseOrEndRecording();
      // handleSubmitAudio(); // We should not submit audio here, it will be submitted when the user clicks the submit button
    }
  }, [isRecording, recordingTime]);

  // Listen for resume recording events from notes list
  React.useEffect(() => {
    const handleResumeEvent = async (...args: unknown[]) => {
      const data = args[0] as { audioId: number; startTime: number };
      console.log(`[RESUME] Resuming recording for audioId: ${data.audioId} at ${data.startTime}s`);

      // Set the recording time to continue from where it left off
      setRecordingTime(data.startTime);
      const devices = await fetchAudioInputDevices();
      dispatch(setAudioDevices(devices));
      dispatch(setRecordingDevice(devices[0]));

      // Set the audio ID
      captureRecordingIdRef.current = data.audioId.toString();

      // Call the CreateNoteModalGCS resume method via ref
      if (createNoteModalRef.current?.resumeRecording) {
        await createNoteModalRef.current.resumeRecording();
      } else {
        console.error("[RESUME] CreateNoteModalGCS resumeRecording method not available");
      }
    };

    eventEmitter.on("resumeRecording", handleResumeEvent);

    return () => {
      eventEmitter.off("resumeRecording", handleResumeEvent);
    };
  }, [dispatch]);

  const handlePauseOrEndRecording = async () => {
    await releaseWakeLock();
    dispatch(setIsRecording(false));
    dispatch(setIsPaused(true));
    if (recordingInterval.current) {
      clearInterval(recordingInterval.current);
      recordingInterval.current = null;
    }

    trackEvent(EventType.STOP_BUTTON, getCaptureDetails());
    if (mediaRecorder?.state === "inactive") {
      return;
    }
    mediaRecorder?.pause();
  };

  const handleResumeRecording = async () => {
    if (!networkState.online) {
      Swal.fire({
        icon: "error",
        title: "Unable to Resume Session",
        text: "It looks like you're currently offline. Make sure your device is connected to Wi-Fi or mobile data before resuming your session.",
      });
      return;
    }
    await requestWakeLock();
    dispatch(setIsPaused(false));
    displayRecordingTimer();
    if (mediaRecorder?.state === "paused") {
      mediaRecorder?.resume();
    } else {
      mediaRecorder?.start(RECORDER_TIMESLICE_MS_GCS);
    }
    dispatch(setIsRecording(true));
    trackEvent(EventType.RESUME_BUTTON, getCaptureDetails());
  };

  const getCaptureDetails = () => {
    const tracks = mediaRecorder?.stream?.getAudioTracks();
    const track = tracks?.length ? tracks[0] : null;
    return {
      modalityMode: recordingDetail.modality_type,
      audioDevices: audioDevices,
      selectedDevice: recordingDevice,
      mediaRecorder: {
        state: mediaRecorder?.state,
        streamActive: mediaRecorder?.stream?.active,
        trackMuted: track ? track.muted : null,
        trackEnabled: track ? track.enabled : null,
        trackReadyState: track ? track.readyState : null,
        mimeType: mediaRecorder?.mimeType,
      },
      isRecording: isRecording,
      audioFileId: captureRecordingIdRef.current || resumableUploadManager.getAudioId()?.toString(),
      hasActiveResumableSession: resumableUploadManager.hasActiveSession(),
    };
  };

  const handleSubmitAudio = () => {
    if (!networkState.online) {
      Swal.fire({
        icon: "error",
        title: "Unable to Submit Recording",
        text: "It looks like you're currently offline. Make sure your device is connected to Wi-Fi or mobile data before submitting your recording.",
      });
      Sentry.captureException(new Error("Unable to submit audio because of offline"), {
        extra: getCaptureDetails(),
      });
      return;
    }
    try {
      if (isTooShort(recordingTime)) {
        Swal.fire({
          icon: "error",
          title: "Recording Too Short",
          text: "This recording is too short. Please record for at least 15 seconds, then try again.",
        });
        return;
      }

      if (isLastPlusOneNote(userInfo)) {
        dispatch(setShowPaymentModal(true));
      }

      dispatch(setSubmissionProgressState(true));
      trackEvent(EventType.SUBMIT_AUDIO_MODAL, getCaptureDetails());

      completeSubmissionGCS(isLastPlusOneNote(userInfo));

      // non blocking upload of audio from indexedDB
      if (captureRecordingIdRef.current) {
        uploadAudioFromIndexedDB(captureRecordingIdRef.current.toString());
      }
    } catch (error) {
      dispatch(setSubmissionProgressState(false));
      Sentry.captureException(error, {
        extra: getCaptureDetails(),
      });
      Swal.fire({
        icon: "error",
        title: "Error submitting audio",
        text: "Please try again or contact support.",
      });
    }
  };

  React.useEffect(() => {
    const isAfterPayment = localStorage.getItem("isPaymentSuccess");
    if (isAfterPayment) {
      dispatch(setOpenShare(true));
      localStorage.removeItem("isPaymentSuccess");
    }
    return () => {
      releaseWakeLock();
    };
  }, []);

  /**
   * If media Recorder is not null, add an error event listener to it
   */
  React.useEffect(() => {
    if (!mediaRecorder) {
      return;
    }
    const errorHandler = (event: any) => {
      Sentry.captureException(event.error);
    };

    mediaRecorder.addEventListener("error", errorHandler);
    return () => {
      mediaRecorder.removeEventListener("error", errorHandler);
    };
  }, [mediaRecorder]);

  const uploadAudioFromIndexedDB = async (audio_id: string) => {
    try {
      const completeAudio = await assembleBlob(audio_id);
      if (!completeAudio) {
        return;
      }
      const contentType = recordMimeType || "audio/webm";
      await uploadAudioFromIndexedDBViaSignedURL(audio_id, completeAudio, contentType);
    } catch (error) {
      const chunkStats = await getChunkStats(audio_id);
      Sentry.captureException(error, {
        extra: {
          audio_id,
          chunkStats,
          error,
          flow: "signed_url_backup_upload",
        },
      });
      // Silent failure - backup upload is not critical
    }
  };

  const completeSubmissionGCS = async (isLocked = false) => {
    if (uploadType === 0 && resumableUploadManager.hasActiveSession()) {
      console.log("Finalizing resumable upload submission");
      try {
        // Stop MediaRecorder first to prevent late chunks from arriving during finalization
        if (mediaRecorder?.state !== "inactive") {
          console.log("Stopping MediaRecorder before finalization");
          stopMediaRecorderAndStopTracks(mediaRecorder);
        }

        // Wait briefly for any final chunks to be processed
        await new Promise((resolve) => setTimeout(resolve, 1000));

        await resumableUploadManager.finalizeUpload(isLocked);

        trackEvent(EventType.SUBMIT_AUDIO_FINAL, {
          audioFileId: resumableUploadManager.getAudioId()?.toString(),
          modalityMode: recordingDetail.modality_type,
          uploadMethod: "resumable",
        });
        message.success("Session submitted successfully. Processing in background...");
      } catch (error) {
        console.error("Error finalizing resumable upload:", error);
        Sentry.captureException(error, { extra: getCaptureDetails() });
        Swal.fire({
          icon: "error",
          title: "Submission Failed",
          text: "We couldn't finalize the submission. Please click on process now button to proceed.",
        });
        dispatch(setSubmissionProgressState(false));
      } finally {
        resetRecordingState();
      }
    } else if (uploadType === 0 && !resumableUploadManager.hasActiveSession()) {
      console.error("Attempted to complete submission but no active resumable session found.");
      Sentry.captureMessage("Submission attempt without active resumable session", {
        extra: getCaptureDetails(),
        level: "warning",
      });
      Swal.fire({
        icon: "error",
        title: "Submission Failed",
        text: "We couldn't finalize the submission. Please click on process now button to proceed.",
      });
      dispatch(setSubmissionProgressState(false));
    } else {
      console.warn("completeSubmission called for unexpected uploadType:", uploadType);
      if (uploadType === 0) resetRecordingState();
    }
  };

  React.useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === "visible") {
        console.log("Page is now in the foreground!");
        await requestWakeLock();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const handleCancel = async () => {
    // This fires when the recording gets cancelled due to any errors in connection or media recorder
    const audioIdToCancel = captureRecordingIdRef.current || resumableUploadManager.getAudioId()?.toString();

    if (audioIdToCancel) {
      try {
        await handleCancelRecording({ audio_id: audioIdToCancel, status: "cancel" });
        await resumableUploadManager.cancelGcsUpload();
        try {
          await clearBuffers(captureRecordingIdRef.current.toString());
        } catch (error) {
          console.error("Error clearing buffers:", error, "audio_id", captureRecordingIdRef.current);
          // pass
        }
      } catch (error) {
        console.error("Error notifying backend of cancellation via handleRetry:", error);
        Sentry.captureException(error, { extra: { audio_id: audioIdToCancel } });
      }
    } else {
      console.warn("handleRetry called without an apparent audio ID to cancel.");
    }
    resetRecordingState();
  };

  const cancelRecording = async () => {
    // This fires when the user clicks the cancel button in the recording view
    Swal.fire({
      title: "Are you sure you want to cancel?",
      text: "You will lose the entire recording, including any previously recorded parts, if you cancel.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Cancel it!",
      cancelButtonText: "No",
    }).then(async (result) => {
      if (result.isConfirmed) {
        trackEvent(EventType.CANCEL_RECORDING, {
          recordingId: captureRecordingIdRef.current,
          modalityType: recordingDetail.modality_type,
          recordingTime: recordingTime,
          isPaused: isPaused,
        });
        handleCancel();
      }
    });
  };

  const items = [
    {
      key: "2",
      label: "Record",
      children: (
        <>
          {isMicrophoneMuted && (
            <Alert message="Microphone is muted. Please unmute to record properly." banner closable />
          )}
          <Col span={24} md={24} xl={24} className={styles[`${componentName}__container-right`]}>
            <Flex vertical align="center">
              {showRecordingView ? (
                <ActiveRecordingView
                  isPaused={isPaused}
                  recordingTime={recordingTime}
                  handlePauseOrEndRecording={handlePauseOrEndRecording}
                  handleResumeRecording={handleResumeRecording}
                  handleSubmitAudio={handleSubmitAudio}
                  cancelRecording={cancelRecording}
                />
              ) : (
                <RecordCTA />
              )}
            </Flex>
          </Col>
        </>
      ),
      icon: <FileAddOutlined />,
    },
    {
      key: "1",
      label: "Notes",
      children: (
        <Col span={24} md={24} className={styles[`${componentName}__container-left`]} id="notes-container-mobile">
          <Outlet />
        </Col>
      ),
      icon: <FileTextOutlined />,
    },
  ];

  React.useEffect(() => {
    const { isNoteModalOpen, isClientModalOpen, recordingDetails, clientForm } = demoState;

    if (isNoteModalOpen || isClientModalOpen) {
      if (isNoteModalOpen) {
        dispatch(setRecordingDetail(recordingDetails));
        dispatch(setCreateModalState(true));
      }
      if (isClientModalOpen) {
        dispatch(setCreateClientState(true));
      }
      if (clientForm) {
        dispatch(setAddClientView(true));
        dispatch(setClientForm(clientForm));
      }
      dispatch(
        setDemoState({
          isNoteModalOpen: false,
          recordingDetails: null,
          clientForm: null,
          isClientModalOpen: false,
        }),
      );
    }
  }, [demoState, dispatch]);

  return (
    <>
      {isMicrophoneMuted && <Alert message="Microphone is muted. Please unmute to record properly." banner closable />}
      <Row className={styles[`${componentName}__container`]}>
        <Col span={24} md={6} lg={6} xl={6} className={styles[`${componentName}__container-side`]}>
          <Flex vertical align="center">
            {showRecordingView ? (
              <ActiveRecordingView
                isPaused={isPaused}
                recordingTime={recordingTime}
                handlePauseOrEndRecording={handlePauseOrEndRecording}
                handleResumeRecording={handleResumeRecording}
                handleSubmitAudio={handleSubmitAudio}
                cancelRecording={cancelRecording}
              />
            ) : (
              <RecordCTA />
            )}
          </Flex>
          <Divider className={styles[`${componentName}__container-side-divider`]} />
          <Flex>
            <ClientView />
          </Flex>
        </Col>
        <Col
          span={24}
          md={18}
          lg={18}
          xl={18}
          className={styles[`${componentName}__container-left`]}
          id="notes-container"
        >
          <Outlet />
        </Col>
        <CreateClient />
        <CreateNoteModalGCS
          isCaptureEnabled={true}
          captureRecordingIdRef={captureRecordingIdRef}
          resetRecordingState={resetRecordingState}
          handleCancel={handleCancel}
          setRecordingTime={setRecordingTime}
          displayRecordingTimer={displayRecordingTimer}
          checkMicrophoneMuteStatus={(stream: MediaStream) => checkMicrophoneMuteStatus(stream, setIsMicrophoneMuted)}
          getCaptureDetails={getCaptureDetails}
          recordingTime={recordingTime}
          ref={createNoteModalRef}
        />
        <AITemplateModal />
        <Share />
      </Row>
      {isMobileView ? (
        <Drawer
          title={customizeModalTitle}
          open={isCustomiseSectionVisible}
          placement="bottom"
          height="85%"
          closable={true}
          destroyOnClose
          onClose={() => dispatch(setCustomiseSectionState(false))}
          width="100%"
        >
          <Flex
            vertical
            justify="space-between"
            style={{
              height: "100%",
            }}
          >
            {notesDetails && (notesDetails.note_type === 0 || notesDetails.note_type === 2) && (
              <Flex className={`${styles[`${componentName}__customise_clearance`]}`}>
                <span />
              </Flex>
            )}
            <CustomiseNote noPadding={true} />
          </Flex>
        </Drawer>
      ) : (
        <Modal
          title={customizeModalTitle}
          open={isCustomiseSectionVisible}
          onCancel={() => dispatch(setCustomiseSectionState(false))}
          footer={null}
          width={450}
          destroyOnClose
          zIndex={999}
          styles={{
            body: {
              height: "600px",
              maxHeight: "80vh",
              overflowY: "auto",
            },
          }}
          style={{
            position: "fixed",
            bottom: "20px",
            right: "20px",
            top: "auto",
            margin: 0,
            paddingBottom: 0,
          }}
        >
          <Flex
            vertical
            justify="space-between"
            style={{
              height: "100%",
            }}
          >
            {notesDetails && (notesDetails.note_type === 0 || notesDetails.note_type === 2) && (
              <Flex className={`${styles[`${componentName}__customise_clearance`]}`}>
                <span />
              </Flex>
            )}
            <CustomiseNote noPadding={true} />
          </Flex>
        </Modal>
      )}
      <Tabs
        destroyInactiveTabPane
        tabPosition={"bottom"}
        size="large"
        items={items}
        className={`${styles[`${componentName}__mobile_view_tabs`]}`}
        centered
        onChange={(key) => {
          if (key === "2") {
            dispatch(setNotesViewVisible(false));
          }
        }}
      />
    </>
  );
};

export default HomePage;
